// The one way a SIP password gets displayed anywhere in Verso.
//
// Every reveal, on every page, requires a fresh acknowledgement — not once per
// session and not once per page. The operator is told what they're about to put
// on screen, ticks the box, and that acknowledgement is what the audit row
// records. The API enforces it too (`ack=1`, 428 without it), so a second UI or a
// script can't route around this dialog.
//
// Use `useRevealSecret()` rather than wiring the fetch yourself: it keeps the
// warning, the audit surface label, and the "hide" behaviour identical
// everywhere, which is the entire point of having one component.

import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

/**
 * @param surface short label for the audit trail — where in the UI this reveal
 *                was triggered (e.g. 'subscriber-extensions', 'extension-detail').
 */
export function useRevealSecret(surface) {
  // Which account is pending acknowledgement, and the value once revealed. Held
  // in component state rather than a query cache so it can't be refetched in the
  // background or re-served after the operator hides it.
  const [pending, setPending] = useState(null);
  const [revealed, setRevealed] = useState(null);

  const reveal = useMutation({
    mutationFn: (acct) => api.get(
      `/sip-accounts/${acct.id}/secret`,
      { ack: 1, from: surface },
    ),
    onSuccess: (data, acct) => {
      setRevealed({ ...data, account: acct });
      setPending(null);
    },
  });

  return {
    /** Open the acknowledgement dialog for an account ({ id, username }). */
    request: (acct) => { reveal.reset(); setRevealed(null); setPending(acct); },
    /** The operator ticked the box and confirmed. */
    confirm: () => { if (pending) reveal.mutate(pending); },
    cancel: () => { setPending(null); reveal.reset(); },
    hide: () => { setRevealed(null); reveal.reset(); },
    pending,
    revealed,
    isPending: reveal.isPending,
    error: reveal.isError
      ? (reveal.error?.body?.error || reveal.error?.message || 'Could not read the password.')
      : null,
  };
}

/** The acknowledgement gate. Renders nothing until `account` is set. */
export function RevealSecretDialog({ account, onConfirm, onCancel, isPending, error }) {
  const [acked, setAcked] = useState(false);

  // The tick must NOT survive a close: "any time, any where" means every reveal
  // is separately acknowledged. This component stays mounted between opens (it
  // just renders null), so without this the second reveal would inherit the
  // first one's consent. Caught by a test that hid and re-opened.
  useEffect(() => { if (account) setAcked(false); }, [account]);

  if (!account) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-lg font-bold text-gray-900">Show SIP password</h3>

        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <p className="font-medium">This displays a credential in plain text.</p>
          <p className="mt-1">
            The SIP password for extension <span className="font-mono">{account.username}</span> lets any
            device register as this customer and place calls billed to them. Only reveal it when you need
            to configure a device, and don&rsquo;t paste it into email, chat or a ticket.
          </p>
        </div>

        <p className="mt-3 text-xs text-gray-500">
          This reveal is recorded in the audit log with your name, the extension, and the time.
        </p>

        <label className="mt-3 flex cursor-pointer items-start gap-2 text-sm text-gray-800">
          <input
            type="checkbox"
            checked={acked}
            onChange={(e) => setAcked(e.target.checked)}
            className="mt-0.5 rounded border-gray-300"
          />
          <span>I understand this is sensitive information and my access will be logged.</span>
        </label>

        {error && (
          <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>
        )}

        <div className="mt-5 flex justify-end gap-3">
          <button
            onClick={() => { setAcked(false); onCancel(); }}
            disabled={isPending}
            className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!acked || isPending}
            className="rounded-md bg-amber-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {isPending ? 'Reading from the PBX…' : 'Show password'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The revealed value. Shows provenance, because FreePBX owns the secret and
 * Verso's copy is a cache — handing over an unconfirmed password that doesn't
 * work is its own kind of failure.
 */
export function RevealedSecret({ revealed, onHide }) {
  const [copied, setCopied] = useState(false);
  if (!revealed) return null;

  return (
    <div className="mt-3 rounded-md border border-amber-200 bg-amber-50/60 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-amber-900">
          SIP password · extension {revealed.account?.username || revealed.username}
        </span>
        <button onClick={onHide} className="text-xs font-medium text-gray-600 hover:underline">
          Hide
        </button>
      </div>

      {revealed.secret ? (
        <>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <code className="select-all rounded border border-gray-300 bg-white px-2 py-1 font-mono text-sm">
              {revealed.secret}
            </code>
            <button
              onClick={() => {
                navigator.clipboard?.writeText(revealed.secret);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
              className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
            <span className={`text-xs ${revealed.source === 'pbx' ? 'text-gray-600' : 'text-amber-800'}`}>
              {revealed.source === 'pbx'
                ? `live from ${revealed.pbx || 'the PBX'}`
                : 'from Verso — not confirmed against the PBX'}
            </span>
          </div>
          {revealed.note && <p className="mt-2 text-xs text-amber-800">{revealed.note}</p>}
          {/* AN UNRECORDED REVEAL IS A DIFFERENT EVENT FROM A RECORDED ONE.
              The agent maintainer, AGENT_ASKS #10, 2026-08-20: the host keeps the reveal available
              when its own audit store is down — it is how somebody configures equipment GDMS cannot
              reach — and says `audited: false` instead of refusing or skipping the trail quietly.
              "Surface that flag wherever a reveal is shown." So: shown, to the person looking at
              the password, because they are the one who would otherwise assume it was logged. */}
          {revealed.host_audited === false && (
            <p className="mt-2 rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
              {revealed.pbx || 'The PBX'} could not write its own audit record for this reveal, so
              there is no trail of it on the host. Verso recorded it here.
            </p>
          )}
        </>
      ) : (
        <p className="mt-2 text-sm text-gray-700">
          {revealed.note || 'No password on file for this extension yet.'}
        </p>
      )}

      <p className="mt-2 text-xs text-gray-500">This reveal was recorded in the audit log.</p>
    </div>
  );
}
